import { useCallback, useEffect, useRef, useState } from "react";
import { useLive } from "../chat/run/live.ts";
import { act, read } from "../net/rally.ts";
import { pressed } from "../sound/mine.ts";

export type CallMetadata = {
  call: string;
  conversation: string;
  state: "upcoming" | "missed" | "live" | "ended";
  startedAt: string | null;
  endedAt: string | null;
};

export type PersonInCall = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  joinedAt: string;
  leftAt: string | null;
  here: boolean;
};

export type InACall = { call: string; conversation: string; title: string | null } | null;

let mine: InACall = null;
const watching = new Set<(held: InACall) => void>();

function nowIn(held: InACall): void {
  mine = held;
  for (const one of [...watching]) one(held);
}

export function membershipAfterCallRead(held: InACall, calls: readonly CallMetadata[]): InACall {
  if (!held) return null;
  const authoritative = calls.find((one) => one.call === held.call);
  return authoritative?.state === "ended" || authoritative?.state === "missed" ? null : held;
}

function reconcileMembership(calls: readonly CallMetadata[]): void {
  const next = membershipAfterCallRead(mine, calls);
  if (next !== mine) nowIn(next);
}

export function useInACall(): InACall {
  const [held, setHeld] = useState<InACall>(mine);
  useEffect(() => {
    watching.add(setHeld);
    setHeld(mine);
    return () => {
      watching.delete(setHeld);
    };
  }, []);
  return held;
}

export function useRetainedCallObserver(): void {
  const held = useInACall();
  const verify = useCallback(() => {
    if (!held) return;
    void read<CallMetadata>("call.read", { call: held.call })
      .then((call) => reconcileMembership([call]))
      .catch(() => undefined);
  }, [held]);
  useEffect(verify, [verify]);
  useLive((notice) => {
    if (notice.conversation === held?.conversation) verify();
  });
}

const A_BEAT_IS = 5000;

export type Calling = {
  available: boolean;

  live: CallMetadata | null;

  here: PersonInCall[];

  joined: boolean;

  start: () => Promise<string | null>;
  join: () => Promise<string | null>;

  leave: () => Promise<void>;

  again: () => void;
};

export function useCalling(conversation: string, title: string | null): Calling {
  const [calls, setCalls] = useState<CallMetadata[]>([]);
  const [available, setAvailable] = useState(true);
  const [here, setHere] = useState<PersonInCall[]>([]);
  const held = useInACall();
  const live = calls.find((one) => one.conversation === conversation && one.state === "live") ?? null;
  const liveId = live?.call ?? null;
  const joined = held !== null && held.call === liveId;

  const again = useCallback(() => {
    void read<{ available: boolean }>("media.mode", {})
      .then((answer) => setAvailable(answer.available))
      .catch(() => setAvailable(false));
    void read<CallMetadata[]>("calls.in", { conversation })
      .then((said) => {
        const inConversation = said.filter((one) => one.conversation === conversation);
        reconcileMembership(inConversation);
        setCalls(inConversation);
      })

      .catch(() => setCalls([]));
  }, [conversation]);

  useEffect(() => {
    setCalls([]);
    setHere([]);
    again();
  }, [again]);

  useLive((notice) => {
    if (notice.conversation === conversation) again();
  });

  const roster = useCallback(() => {
    if (!liveId) {
      setHere([]);
      return;
    }
    void read<PersonInCall[]>("call.roster", { call: liveId })
      .then((said) => setHere(said))
      .catch(() => setHere([]));
  }, [liveId]);

  useEffect(() => {
    roster();
    if (!liveId) return;
    const timer = setInterval(roster, A_BEAT_IS);
    return () => clearInterval(timer);
  }, [liveId, roster]);

  const inRoom = useRef<string | null>(null);
  inRoom.current = joined ? liveId : null;
  useEffect(() => {
    if (!joined || !liveId) return;
    const beat = () => void act("call.here", { call: liveId }).catch(() => undefined);
    beat();
    const timer = setInterval(beat, A_BEAT_IS);
    return () => clearInterval(timer);
  }, [joined, liveId]);

  const enter = useCallback(
    async (call: string): Promise<string> => {
      pressed("joined", conversation);
      await act("call.join", { call });
      nowIn({ call, conversation, title });
      again();
      return call;
    },
    [conversation, title, again],
  );

  const join = useCallback(async (): Promise<string | null> => {
    if (!liveId) return null;
    return enter(liveId);
  }, [liveId, enter]);

  const start = useCallback(async (): Promise<string | null> => {
    if (liveId) return enter(liveId);
    const made = await act<{ id: string }>("call.schedule", { conversation });
    pressed("started", conversation);
    pressed("joined", conversation);
    await act("call.start", { call: made.id });
    nowIn({ call: made.id, conversation, title });
    again();
    return made.id;
  }, [liveId, conversation, title, enter, again]);

  const leave = useCallback(async (): Promise<void> => {
    const call = held?.call ?? liveId;
    if (!call) return;
    nowIn(null);
    pressed("left", conversation);
    await act("call.leave", { call }).catch(() => undefined);

    const left = await read<PersonInCall[]>("call.roster", { call }).catch(() => [] as PersonInCall[]);
    if (!left.some((one) => one.here)) {
      pressed("ended", conversation);
      await act("call.end", { call }).catch(() => undefined);
    }
    again();
  }, [held, liveId, conversation, again]);

  return { available, live, here, joined, start, join, leave, again };
}

export function forgetTheCall(): void {
  nowIn(null);
}
